import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, IconButton, Typography, Box, MenuItem, Select
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  ArcElement,
  PointElement,
  Tooltip,
  Legend
);

interface ChartModalProps {
  open: boolean;
  onClose: () => void;
  headers: string[];
  rows: Record<string, any>[];
  x_axis: string;
  y_axis: string | string[];
  chart_type: 'bar' | 'line' | 'pie';
}

const ChartModal: React.FC<ChartModalProps> = ({
  open,
  onClose,
  headers,
  rows,
  x_axis,
  y_axis,
  chart_type,
}) => {
  const [currentType, setCurrentType] = useState<'bar' | 'line' | 'pie'>(chart_type);
  const palette = ['#1976d2', '#d32f2f'];

  const normalizeValue = (value: any): number => {
    if (typeof value === 'string') {
      const cleaned = value.replace(/[€\s]/g, '').replace(',', '.');
      return parseFloat(cleaned) || 0;
    }
    return typeof value === 'number' ? value : 0;
  };

  if (!headers.length || !rows.length || !x_axis || !y_axis.length)
    return (
      <Dialog open={open} onClose={onClose}>
        <DialogTitle>⚠️ Non è possibile creare un grafico</DialogTitle>
        <DialogContent>
          I dati non contengono colonne utili per il grafico.
        </DialogContent>
      </Dialog>
    );

  const yFields = Array.isArray(y_axis) ? y_axis : [y_axis];

  const datasets =
    currentType === 'pie'
      ? []
      : yFields.map((field, index) => ({
          label: field,
          data: rows.map((r) => normalizeValue(r[field]) || 0),
          backgroundColor:
            currentType === 'bar'
              ? palette[index % palette.length]
              : undefined,
          borderColor:
            currentType === 'line'
              ? `rgba(${index === 0 ? '25, 118, 210' : '211, 47, 47'}, 0.7)`
              : undefined,
          tension: currentType === 'line' ? 0.4 : 0,
        }));

  const ChartComponent =
    currentType === 'bar' ? Bar : currentType === 'line' ? Line : Pie;

  const chartData = {
    labels: rows.map((r) => String(r[x_axis] ?? '–')),
    datasets:
      currentType === 'pie'
        ? [
            {
              label: yFields[0],
              data: rows.map((r) => normalizeValue(r[yFields[0]]) || 0),
              backgroundColor: rows.map(
                (_, i) => `hsl(${(i * 37) % 360}, 55%, 70%)`
              ),
              borderColor: '#ffffff',
              borderWidth: 1,
            },
          ]
        : datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      tooltip: {
        backgroundColor: '#374151',
        titleFont: { size: 14, weight: 700 },
        bodyFont: { size: 13 },
        padding: 10,
        cornerRadius: 4,
      },
      legend: {
        display: currentType === 'pie',
        position: 'bottom' as const,
      },
    },
    scales:
      currentType !== 'pie'
        ? {
            x: {
              ticks: {
                color: '#374151',
                font: { size: 12 },
              },
              grid: { display: false },
            },
            y: {
              ticks: {
                color: '#374151',
                font: { size: 12 },
              },
              grid: { color: '#e5e7eb' },
            },
          }
        : {},
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: 3,
          backgroundColor: '#f9fafb',
          boxShadow: '0 12px 30px rgba(0,0,0,0.1)',
          height: '75vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          pt: 2,
          pb: 1,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600, color: '#111827' }}>
          📊 Grafico: {yFields.join(' + ')} rispetto a {x_axis}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Select
            size="small"
            value={currentType}
            onChange={(e) =>
              setCurrentType(e.target.value as 'bar' | 'line' | 'pie')
            }
            sx={{ minWidth: 100 }}
          >
            <MenuItem value="bar">Bar</MenuItem>
            <MenuItem value="line">Line</MenuItem>
            <MenuItem value="pie">Pie</MenuItem>
          </Select>
          <IconButton onClick={onClose} size="small">
            <CloseIcon sx={{ color: '#6b7280' }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 3 }}>
        <Box sx={{ height: '100%', minHeight: 400 }}>
          <ChartComponent data={chartData} options={options} />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ChartModal;
